import type { CgOrder } from './types';

/**
 * Whether a factory is currently waiting on TAP to answer a pushback.
 *
 * The one fact this whole feature turns on, kept here rather than inlined so
 * the board, the table and the drawer cannot drift into three different
 * readings of the same two columns.
 *
 * ── Why two timestamps and not a flag ──────────────────────────────────────
 *
 * A boolean would have to be cleared by TAP and set by the supplier, and the
 * supplier's write is the one nobody was seeing in the first place. Two
 * timestamps make "outstanding" a comparison instead of a state anybody has to
 * remember to reset — and, crucially, a SECOND request after TAP has already
 * answered the first reads as outstanding again for free. With a flag, the
 * factory's second problem lands on an order somebody already ticked off.
 *
 * Orders carrying a note from before 20261020000100 have no request timestamp
 * at all. Those read as handled, which is the truthful answer: nobody has
 * raised anything since the system started counting.
 */
export function revisionOutstanding(order: CgOrder): boolean {
  const requested = stampOf(order.supplier_revision_at);
  if (requested === '') {
    return false;
  }
  const acknowledged = stampOf(order.supplier_revision_ack_at);
  if (acknowledged === '') {
    return true;
  }
  // Strictly earlier. Equal means the acknowledgement is the answer to this
  // request, not to a previous one — the database writes clock_timestamp() on
  // both sides precisely so these can be ordered.
  //
  // String comparison, not Date: both values are ISO-8601 UTC from the same
  // column type, and ISO-8601 sorts lexicographically in exactly the order it
  // sorts chronologically. Parsing would add a way to get NaN and no accuracy.
  return acknowledged < requested;
}

/**
 * A timestamp column narrowed to "present and usable", or the empty string.
 *
 * PRESENT, not merely "not null". A row that predates these columns — one
 * built by an older cached query, a fixture, or any caller that does not know
 * about them — has the field ABSENT, and `=== null` is false for undefined.
 * Left unguarded, an absent acknowledgement fell through to `undefined <
 * '2026-…'`, which JavaScript answers false, so a live request read as
 * handled. That is the failure this whole feature exists to prevent, arriving
 * by a different door.
 *
 * The same trap notificationHref documents, one file over. Worth naming twice.
 */
function stampOf(value: string | null): string {
  return typeof value === 'string' ? value : '';
}

/**
 * What the drawer says above the note.
 *
 * Empty when there is nothing to say, so the caller renders nothing at all
 * rather than an empty panel with a heading.
 */
export function revisionSummary(order: CgOrder): string {
  if (order.supplier_revision_note.trim() === '') {
    return '';
  }
  if (revisionOutstanding(order)) {
    return 'The supplier has asked for a change to this PO and is waiting on an answer.';
  }
  // Still worth showing. The conversation happened and the note is the record
  // of it; hiding it once handled means the next person to open the order has
  // no idea why the target date moved.
  return 'The supplier asked for a change to this PO. Somebody at TAP has dealt with it.';
}

/** Orders a factory is blocked on, for a count somebody can act on. */
export function ordersAwaitingRevisionAnswer(orders: CgOrder[]): CgOrder[] {
  return orders.filter(revisionOutstanding);
}
